/**
 * Authentication Service
 * Gestisce autenticazione Google Workspace con restrizioni dominio polo9.org
 * e verifica appartenenza ai gruppi autorizzati
 */

import { parseStructureAccess } from '@/features/dipendenze/utils/structureScope';
import { logger } from '@/lib/logger';
import { 
  auth, 
  loginWithGoogle as firebaseLoginWithGoogle,
  logout as firebaseLogout 
} from '@/lib/firebase';
import { User } from 'firebase/auth';

import { ADMIN_SERVICE_URL } from '@/config/endpoints';
import {
  getHubResources,
  getAppsByHub,
  hasPermission,
  getAllResources,
  deriveImpliedPermissions,
  getFullHubAccessPermissions
} from '@/config/accessControlCatalog';
import { HubId, AppId } from '@/types/hub-config';
import { UserPermissions, HubPermissionCalculated, AppPermissionCalculated } from '@/types/hub-permissions';

// Configurazione dominio autorizzato
const ALLOWED_DOMAIN = 'polo9.org';

// Ruoli utente (definiti anche nel backend)

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  UNAUTHORIZED = 'unauthorized'
}

export interface AuthenticatedUser {
  uid: string;
  email: string;
  displayName: string | null;
  photoURL: string | null;
  domain: string;
  role: UserRole;
  groups: string[];
  /** Compatibilità con il ruolo legacy role: admin */
  isAdmin: boolean;
  /** Accesso esplicito all'hub Amministrazione, verificato dal backend */
  isPlatformAdmin: boolean;
  /** Aree amministrative assegnate al gruppo; '*' indica compatibilità legacy completa */
  platformAdminAreas?: string[];
  /** true se l'accesso admin proviene dal modello legacy */
  isLegacyAdmin: boolean;
  isSuperAdmin: boolean;
  /** true solo dopo una risposta autorevole del backend effective */
  platformAccessVerified: boolean;
  /** Hub per cui l'utente è Admin di Area (accesso completo senza essere Super Admin) */
  adminScope: string[];
  isAuthorized: boolean;
  /** Per applicazione Dipendenze: true se l'utente ha almeno una struttura nel proprio ambito (assente se non calcolato) */
  dipendenzeStructureAccess?: Record<string, boolean>;
  // Nuovo: permessi Hub/App
  permissions: import('@/types/permissions').UserPermissions;
  scopes?: string[];
  canFilterOtherScopes?: boolean;
  canSeeTotals?: boolean;
  // Nuovo: permessi piattaforma string-based dal token
  platformPermissions?: string[];
  // Permessi nel formato Access Control (resource:action)
  accessControlPermissions?: string[];
}

type HubPermissionLog = {
  hubId?: string;
  canView?: boolean;
  apps?: Record<string, unknown>;
};

type AuthenticatedUserWithDashboardFlags = AuthenticatedUser & {
  canFilterOtherScopes: boolean;
  canSeeTotals: boolean;
};

/**
 * Verifica se l'email appartiene al dominio autorizzato
 */
function isAllowedDomain(email: string): boolean {
  const domain = email.split('@')[1];
  return domain === ALLOWED_DOMAIN;
}

type ClaimsIdentity = {
  role: UserRole;
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  platformAdminAreas: string[];
  scopes: string[];
  permissions?: string[];
  hubAccess?: string[];
};

/**
 * Verifica i Custom Claims dell'utente dal JWT token
 * Restituisce il ruolo assegnato dai Custom Claims
 */
async function getUserRoleFromClaims(user: User): Promise<ClaimsIdentity> {
  // In sviluppo con Auth emulator, salta la verifica e assegna ruolo admin
  const useAuthEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_AUTH_EMULATOR === 'true';
  if (useAuthEmulator) {
    logger.debug('🔧 Auth emulator attivo: assegno ruolo admin senza verifica gruppi');
    return {
      scopes: ['*'],
      isSuperAdmin: true,
      isPlatformAdmin: true,
      platformAdminAreas: ['*'],
      role: UserRole.ADMIN
    };
  }
  
  try {
    logger.debug('🔍 Lettura Custom Claims dal token JWT (force refresh)');
    const tokenResult = await user.getIdTokenResult(true);
    const claims = tokenResult.claims;
    
    if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
      logger.debug('Auth Debug - token claims summary', {
        email: user.email,
        role: claims.role,
        scopes: Array.isArray(claims.scopes) ? claims.scopes : [],
        permissions: Array.isArray(claims.permissions) ? claims.permissions : [],
        updatedAt: claims.updatedAt,
        claimKeys: Object.keys(claims || {})
      });
    }

    const claimPermissions = Array.isArray(claims.permissions) ? claims.permissions : [];
    const claimHubAccess = Array.isArray(claims.hub_access) ? claims.hub_access.filter((hub): hub is string => typeof hub === 'string') : [];
    const claimPlatformAdminAreas = Array.isArray(claims.platform_admin_areas)
      ? claims.platform_admin_areas.filter((area): area is string => typeof area === 'string')
      : [];

    const isSuperAdminClaim = claims.is_super_admin === true;

    // Verifica se ha il claim 'role'
    if (claims.role === 'admin') {
      return {
        scopes: Array.isArray(claims.scopes) ? claims.scopes : [],
        permissions: claimPermissions,
        hubAccess: claimHubAccess,
        isSuperAdmin: isSuperAdminClaim,
        isPlatformAdmin: claims.is_platform_admin === true,
        platformAdminAreas: claimPlatformAdminAreas,
        role: UserRole.ADMIN
      };
    } else if (claims.role === 'user') {
      return {
        scopes: Array.isArray(claims.scopes) ? claims.scopes : [],
        permissions: claimPermissions,
        hubAccess: claimHubAccess,
        isSuperAdmin: isSuperAdminClaim,
        isPlatformAdmin: claims.is_platform_admin === true,
        platformAdminAreas: claimPlatformAdminAreas,
        role: UserRole.USER
      };
    } else {
      // Nessun claim role = accesso base come USER (solo home e documenti)
      logger.warn('Nessun Custom Claim "role" trovato, assegno ruolo USER base');
      return {
        scopes: [],
        permissions: claimPermissions,
        hubAccess: claimHubAccess,
        isSuperAdmin: isSuperAdminClaim,
        isPlatformAdmin: claims.is_platform_admin === true,
        platformAdminAreas: claimPlatformAdminAreas,
        role: UserRole.USER
      };
    }
  } catch (error) {
    logger.error('Errore lettura Custom Claims:', error);
    return {
      scopes: [],
      isSuperAdmin: false,
      isPlatformAdmin: false,
      platformAdminAreas: [],
      role: UserRole.UNAUTHORIZED
    };
  }
}

/**
 * Verifica completa dell'utente autenticato
 * Il ruolo viene determinato dai Custom Claims nel JWT token
 */
export async function verifyAuthenticatedUser(user: User): Promise<AuthenticatedUser | null> {
  const email = user.email;
  
  logger.debug('🔐 Verifica autenticazione per:', email);
  
  if (!email) {
    logger.error('Utente senza email');
    return null;
  }
  
  // Verifica dominio
  if (!isAllowedDomain(email)) {
    logger.warn('Dominio non autorizzato');
    // L'utente verrà reindirizzato alla pagina Unauthorized
    return null;
  }
  
  // Leggi ruolo e is_super_admin dai Custom Claims
  const claimsIdentity = await getUserRoleFromClaims(user);
  const {
    scopes: claimScopes,
    permissions: claimPermissions,
    hubAccess: claimHubAccess,
    isSuperAdmin: claimIsSuperAdmin,
    isPlatformAdmin: claimIsPlatformAdmin
  } = claimsIdentity;
  let role = claimsIdentity.role;
  
  logger.debug(`Ruolo assegnato: ${role}, Platform Admin claim: ${claimIsPlatformAdmin}, Scopes claims: ${claimScopes.join(', ')}`);
  if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
    logger.debug('Auth Debug - effective auth identity before permission calculation', {
      email,
      role,
      claimScopes
    });
  }
  
  // Tutti gli utenti polo9.org possono accedere (almeno come USER base)

  // Carica i permessi effettivi dal backend (fonte autoritativa)
  const compactHubPermissions = (claimHubAccess || [])
    .filter((hubId) => hubId !== '*')
    .flatMap((hubId) => getFullHubAccessPermissions(hubId));
  let accessControlPermissions: string[] = claimIsSuperAdmin
    ? ['*']
    : deriveImpliedPermissions([...(claimPermissions || []), ...compactHubPermissions]);
  let effectiveIsSuperAdmin = claimIsSuperAdmin;
  let effectiveIsPlatformAdmin = false;
  let effectivePlatformAdminAreas: string[] = claimsIdentity.platformAdminAreas || [];
  let platformAccessVerified = false;
  // Admin di Area: hub per cui l'utente ha accesso completo (senza essere Super Admin)
  let adminScope: string[] = [];
  let dipendenzeStructureAccess: Record<string, boolean> | undefined;

  try {
    const idToken = await user.getIdToken();
    const res = await fetch(`${ADMIN_SERVICE_URL}/access-control/effective`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ data: { email, uid: user.uid } })
    });
    if (res.ok) {
      const json = await res.json();
      effectiveIsSuperAdmin = json.result?.isSuperAdmin === true;
      effectiveIsPlatformAdmin = json.result?.isPlatformAdmin === true;
      const sourceAreas = json.result?.sources?.platformAdminAreas || {};
      const unscoped = json.result?.sources?.unscopedPlatformAdminGroups || [];
      const rawAreas = [
        ...Object.values(sourceAreas).flat(),
        ...(json.result?.sources?.userPlatformAdminAreas || []),
      ] as unknown[];
      effectivePlatformAdminAreas = unscoped.length > 0 || rawAreas.includes('*')
        ? ['*']
        : Array.from(new Set(rawAreas.filter((area): area is string => typeof area === 'string')));
      platformAccessVerified = true;
      adminScope = Array.isArray(json.result?.adminScope) ? json.result.adminScope : [];
      dipendenzeStructureAccess = parseStructureAccess(json.result?.dipendenzeStructureAccess);
      const backendPermissions = effectiveIsSuperAdmin
        ? ['*']
        : (json.result?.permissions || []);
      accessControlPermissions = effectiveIsSuperAdmin
        ? ['*']
        : backendPermissions;
    } else {
      logger.warn('⚠️ Errore caricamento permessi effettivi dal backend, uso claims fallback');
    }
  } catch (error) {
    logger.warn('⚠️ Impossibile contattare admin-service per i permessi effettivi:', error);
  }

  // Se il backend indica super admin, forza ruolo admin
  if (effectiveIsSuperAdmin) {
    role = UserRole.ADMIN;
  }

  let effectiveWorkspaceGroups: string[] = [];

  try {
    // Carica i gruppi Workspace dell'utente dal nuovo modello Access Control
    const { db } = await import('@/lib/firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    const userAccessDoc = await getDoc(doc(db, 'system_config/access_control/users', email));
    const userAccessData = userAccessDoc.exists() ? userAccessDoc.data() : null;
    effectiveWorkspaceGroups = Array.isArray(userAccessData?.groups) ? userAccessData.groups : [];
  } catch (error) {
    logger.warn('⚠️ Impossibile caricare gruppi Workspace dal nuovo modello Access Control:', error);
    effectiveWorkspaceGroups = [];
  }

  // Costruisce la rappresentazione legacy compatibile a partire dai permessi Access Control
  const hubPermissions = buildLegacyPermissions(accessControlPermissions, role);

  if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
    logger.debug('Auth Debug - Access Control permissions', {
      email,
      accessControlPermissions,
      visibleHubs: hubPermissions.hubs.map((h) => h.hubId)
    });
  }

  // Estrai scopes Dashboard dai permessi per PermissionsContext
  let dashboardScopes: string[] = [];
  let canFilterOtherScopes = false;
  let canSeeTotals = true;
  
  // Cerca permessi Dashboard in Business hub
  const businessHub = hubPermissions.hubs.find(h => h.hubId === 'business');
  if (businessHub?.apps?.dashboard) {
    const dashboardPerms = businessHub.apps.dashboard as unknown as {
      full_access?: boolean;
      scopes?: string[];
      can_filter_other_scopes?: boolean;
      can_see_totals?: boolean;
      canAccess?: boolean;
    };
    if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
      logger.debug('Auth Debug - raw dashboard permission object', {
        email,
        dashboardPerms
      });
    }

    // [1A.18.5-fix-critical-b] Solo Super Admin o full_access esplicito danno scopes globali.
    // Un Admin di Area sul hub business ha per definizione accesso completo ai dati.
    if (effectiveIsSuperAdmin || dashboardPerms.full_access || adminScope.includes('business')) {
      dashboardScopes = ['*'];
      canFilterOtherScopes = true;
    } else if (dashboardPerms.scopes && Array.isArray(dashboardPerms.scopes)) {
      dashboardScopes = dashboardPerms.scopes;
      canFilterOtherScopes = dashboardPerms.can_filter_other_scopes || false;
      canSeeTotals = dashboardPerms.can_see_totals !== false;
    }
  }

  const authenticatedUser: AuthenticatedUserWithDashboardFlags = {
    uid: user.uid,
    email: email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    domain: email.split('@')[1],
    role: role,
    groups: effectiveWorkspaceGroups,
    isAdmin: role === UserRole.ADMIN,
    isPlatformAdmin: effectiveIsPlatformAdmin,
    platformAdminAreas: effectivePlatformAdminAreas,
    isLegacyAdmin: role === UserRole.ADMIN && !effectiveIsPlatformAdmin && !effectiveIsSuperAdmin,
    isSuperAdmin: effectiveIsSuperAdmin,
    platformAccessVerified,
    adminScope,
    dipendenzeStructureAccess,
    isAuthorized: true,
    permissions: hubPermissions,
    // Aggiungi scopes per PermissionsContext
    scopes: dashboardScopes,
    canFilterOtherScopes: canFilterOtherScopes,
    canSeeTotals: canSeeTotals,
    // [1A.18.5-profiles-plumbing] Unisci platform permissions dai claims e dai profili
    platformPermissions: accessControlPermissions,
    accessControlPermissions
  };
  const authenticatedBusinessHub = authenticatedUser.permissions?.hubs?.find((h) => h.hubId === 'business') as HubPermissionLog | undefined;
  if (import.meta.env.VITE_AUTH_DEBUG === 'true') {
    logger.debug('Auth Debug - final authenticated user snapshot', {
      email,
      role: authenticatedUser.role,
      groups: authenticatedUser.groups,
      businessHub: authenticatedBusinessHub || null,
      scopes: authenticatedUser.scopes,
      canFilterOtherScopes: authenticatedUser.canFilterOtherScopes,
      canSeeTotals: authenticatedUser.canSeeTotals,
      platformPermissions: authenticatedUser.platformPermissions
    });
  }

  logger.debug('Utente autenticato con successo');
  
  return authenticatedUser;
}

/**
 * Login con Google e verifica autorizzazioni
 */
export async function login(): Promise<AuthenticatedUser | null> {
  try {
    // Esegui login Google
    await firebaseLoginWithGoogle();
    
    // Attendi che l'utente sia autenticato
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Login fallito: nessun utente autenticato');
    }
    
    // Verifica autorizzazioni
    const authenticatedUser = await verifyAuthenticatedUser(user);
    
    if (!authenticatedUser) {
      // Se non autorizzato, esegui logout
      await firebaseLogout();
      throw new Error('Accesso negato: utente non autorizzato');
    }
    
    return authenticatedUser;
  } catch (error) {
    logger.error('Errore durante il login:', error);
    throw error;
  }
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
  await firebaseLogout();
}

/**
 * Ottieni l'utente corrente autenticato con verifica autorizzazioni
 */
export async function getCurrentAuthenticatedUser(): Promise<AuthenticatedUser | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  
  return await verifyAuthenticatedUser(user);
}

/**
 * Verifica se l'utente corrente è amministratore
 */
export async function isCurrentUserAdmin(): Promise<boolean> {
  const user = await getCurrentAuthenticatedUser();
  return user?.isAdmin ?? false;
}

/**
 * Verifica se l'utente ha un permesso string-based specifico
 * o è Super Admin / ha wildcard.
 * [1A.18.1-quater] Il bypass isAdmin (role=admin) è stato RIMOSSO:
 * un admin delegato vede solo ciò per cui ha permessi espliciti.
 */
export function hasPlatformPermission(
  user: AuthenticatedUser | null,
  permission: string
): boolean {
  if (!user || !user.isAuthorized) return false;
  if (user.isSuperAdmin) return true;
  if (user.scopes?.includes('*')) return true;

  const perms = user.accessControlPermissions || user.platformPermissions || [];
  if (perms.includes('*')) return true;

  // Supporta sia vecchio formato (permission:manage) che nuovo (platform:permission:manage:execute)
  const normalized = `platform:${permission}:execute`;
  return perms.includes(permission) || perms.includes(normalized) ||
    hasPermission(perms, normalized);
}

/**
 * Verifica permessi per azione specifica (legacy)
 * Preferire hasPlatformPermission per nuovi controlli
 */
export async function checkPermission(action: string): Promise<boolean> {
  const user = await getCurrentAuthenticatedUser();
  
  if (!user || !user.isAuthorized) {
    return false;
  }
  
  // Azioni riservate agli admin
  const adminOnlyActions = [
    'manage_users',
    'view_settings',
    'modify_settings',
    'delete_dataset',
    'view_audit_logs'
  ];
  
  if (adminOnlyActions.includes(action)) {
    return user.isAdmin;
  }
  
  // Tutte le altre azioni sono permesse agli utenti autorizzati
  return true;
}

/**
 * Middleware per proteggere route che richiedono autenticazione
 */
export async function requireAuth(): Promise<AuthenticatedUser> {
  const user = await getCurrentAuthenticatedUser();
  
  if (!user) {
    throw new Error('Autenticazione richiesta');
  }
  
  return user;
}

/**
 * Middleware per proteggere route che richiedono privilegi admin
 */
export async function requireAdmin(): Promise<AuthenticatedUser> {
  const user = await requireAuth();
  
  if (!user.isAdmin) {
    logger.warn('Tentativo accesso admin non autorizzato');
    throw new Error('Privilegi amministratore richiesti');
  }
  
  return user;
}

/**
 * Costruisce una rappresentazione compatibile con il sistema precedente
 * a partire dai permessi Access Control (resource:action).
 */
function buildLegacyPermissions(permissions: string[], role: UserRole): UserPermissions {
  const hubs: HubPermissionCalculated[] = [];

  for (const hub of getHubResources()) {
    const hubId = hub.hubId as HubId;
    const apps: Record<AppId, AppPermissionCalculated> = {};

    for (const app of getAppsByHub(hub.hubId!)) {
      const appId = app.appId as AppId;
      const canAccess = hasPermission(permissions, `app:${hub.hubId}.${app.appId}:view`);

      if (!canAccess) {
        apps[appId] = { canAccess: false };
        continue;
      }

      const crud = {
        create: hasPermission(permissions, `app:${hub.hubId}.${app.appId}:create`),
        read: hasPermission(permissions, `app:${hub.hubId}.${app.appId}:read`),
        update: hasPermission(permissions, `app:${hub.hubId}.${app.appId}:update`),
        delete: hasPermission(permissions, `app:${hub.hubId}.${app.appId}:delete`)
      };

      // Dashboard business: mappa scope/flag dai nuovi permessi.
      // Deve rispecchiare esattamente la logica autoritativa lato server
      // (admin-service/services/accessControlService.js#getEffectiveDashboardAccess):
      // - "Gestione e Controllo" (dashboard_full_access) abilita tab + perimetro dati completo.
      // - Il vecchio flag "Leggi dati sensibili business" (read_sensitive) dà SOLO il
      //   perimetro dati completo, non le tab Dettagli/Analisi.
      if (hub.hubId === 'business' && app.appId === 'dashboard') {
        const tabsFullAccess = hasPermission(permissions, 'platform:business:dashboard_full_access:execute');
        const dataFullAccess = tabsFullAccess || hasPermission(permissions, 'platform:business:read_sensitive:execute');
        const scopeResources = getAllResources().filter(
          (r) => r.type === 'scope' && hasPermission(permissions, `${r.id}:view`)
        );
        const scopes = dataFullAccess ? ['*'] : scopeResources.map((r) => r.label || r.id);
        const canAccessDettagli = tabsFullAccess || hasPermission(permissions, 'feature:business.dashboard.dettagli:view');
        const canAccessAnalisi = tabsFullAccess || hasPermission(permissions, 'feature:business.dashboard.analisi:view');
        // Filtro CDC: puramente una funzionalità UI (il perimetro dati reale è
        // comunque sempre applicato server-side indipendentemente da questo
        // flag), quindi concesso anche con "Gestione e Controllo" o con il
        // permesso esplicito dedicato.
        const canFilterCdc = tabsFullAccess || hasPermission(permissions, 'feature:business.dashboard.filtro_cdc:view');

        apps[appId] = {
          canAccess: true,
          full_access: dataFullAccess,
          scopes,
          can_filter_cdc: canFilterCdc,
          can_filter_other_scopes: dataFullAccess,
          can_see_totals: true,
          can_access_dettagli: canAccessDettagli,
          can_access_analisi: canAccessAnalisi
        };
      } else {
        apps[appId] = { canAccess: true, crud };
      }
    }

    hubs.push({
      hubId,
      canView: hasPermission(permissions, `hub:${hub.hubId}:view`),
      apps
    });
  }

  return {
    role: role === UserRole.ADMIN ? 'admin' : 'user',
    hubs,
    platformPermissions: permissions
  };
}
